class BundleCardDrawer {
  constructor() {
    this.bundle = new Map()
    this.init()
  }

  init() {
    this.setupEventListeners()
    this.setupFeaturedCarousel()
    this.renderAddedProducts()
  }

  setupEventListeners() {
    // Listen for variant changes to update button text
    document.addEventListener('bundle:variant-selected', (e) => {
      const drawer = e.target.closest('[data-bundle-drawer]')
      if (!drawer) return

      const button = document.querySelector(`[aria-controls="${drawer.id}"]`)
      if (!button) return

      // Don't update if product is already in bundle
      if (this.bundle.has(button.dataset.productId)) {
        return
      }

      const isMobile = window.innerWidth < 990
      const drawerAddBtn = drawer.querySelector('[data-bundle-drawer-add]')

      if (e.detail.hasVariant && e.detail.variant) {
        const variant = e.detail.variant
        const isAvailable = variant.available

        // Always store variant data on card button
        button.dataset.variantId = variant.id
        button.dataset.variantPrice = variant.price

        // Mark that user has interacted with variant selection
        // (Only set if triggered by user interaction, not initial load)
        if (e.detail.userTriggered) {
          button.dataset.userChosen = 'true'
        }

        // Desktop only: update card button appearance
        if (!isMobile) {
          const buttonText = button.querySelector('.card-bundle__button-text')
          if (buttonText) {
            buttonText.textContent = isAvailable ? 'Add to look' : 'Sold out'
          }
          button.classList.remove('button--primary')
          button.classList.add('button--secondary')
          // Disable button if sold out
          button.disabled = !isAvailable
        }

        // Enable/disable in-drawer add button based on availability
        if (drawerAddBtn) {
          drawerAddBtn.disabled = !isAvailable
          const drawerBtnText = drawerAddBtn.querySelector('span') || drawerAddBtn
          if (drawerBtnText.tagName === 'SPAN') {
            drawerBtnText.textContent = isAvailable ? 'Add to look' : 'Sold out'
          } else {
            drawerAddBtn.textContent = isAvailable ? 'Add to look' : 'Sold out'
          }
        }
      } else {
        delete button.dataset.variantId
        delete button.dataset.variantPrice

        // Desktop only: reset card button appearance
        if (!isMobile) {
          const buttonText = button.querySelector('.card-bundle__button-text')
          if (buttonText) buttonText.textContent = 'Select options'
          button.classList.remove('button--secondary')
          button.classList.add('button--primary')
          button.disabled = false // Re-enable button
        }

        // Disable in-drawer add button and reset text
        if (drawerAddBtn) {
          drawerAddBtn.disabled = true
          const drawerBtnText = drawerAddBtn.querySelector('span') || drawerAddBtn
          if (drawerBtnText.tagName === 'SPAN') {
            drawerBtnText.textContent = 'Add to look'
          } else {
            drawerAddBtn.textContent = 'Add to look'
          }
        }
      }
    })

    // Handle all clicks
    document.addEventListener('click', (e) => {
      // Button clicks
      const button = e.target.closest('[data-bundle-card-trigger]')
      console.log('click')
      if (button) {
        e.preventDefault()
        this.handleButtonClick(button)
        return
      }

      // In-drawer add button (mobile)
      const drawerAddBtn = e.target.closest('[data-bundle-drawer-add]')
      if (drawerAddBtn) {
        e.preventDefault()
        const drawer = drawerAddBtn.closest('[data-bundle-drawer]')
        if (drawer) {
          const cardButton = document.querySelector(`[aria-controls="${drawer.id}"]`)
          if (cardButton) {
            // Ensure variant data is current from bundle radios
            // This handles the case where user clicks "Add to look" without changing any options
            const bundleRadios = drawer.querySelector('bundle-variant-radios')
            if (bundleRadios?.currentVariant) {
              cardButton.dataset.variantId = bundleRadios.currentVariant.id
              cardButton.dataset.variantPrice = bundleRadios.currentVariant.price
            }

            // Mark that user has explicitly chosen from the drawer
            cardButton.dataset.userChosen = 'true'
            this.addToBundle(cardButton)
          }
        }

        // If the product card has data-hide-sticky-atc attribute, hide the PDP sticky ATC button on click
        const stickyAtc = document.querySelector('[data-sticky-atc]')
        if (stickyAtc) {
          stickyAtc.classList.remove('is-visible')
          stickyAtc.setAttribute('data-hidden', '')
        }
        return
      }

      // Close button
      const closeBtn = e.target.closest('[data-bundle-drawer-close]')
      if (closeBtn) {
        const drawer = closeBtn.closest('[data-bundle-drawer]')
        if (drawer) {
          this.closeDrawer(drawer)
          // Reset button state when closing without adding
          this.resetButtonStateForDrawer(drawer)
        }
        return
      }

      // Overlay click
      const overlay = e.target.closest('[data-bundle-drawer-overlay]')
      if (overlay) {
        const drawer = overlay.closest('[data-bundle-drawer]')
        if (drawer) {
          this.closeDrawer(drawer)
          // Reset button state when closing without adding
          this.resetButtonStateForDrawer(drawer)
        }
        return
      }

      // Remove button
      const removeBtn = e.target.closest('[data-bundle-remove]')
      if (removeBtn) {
        this.removeFromBundle(removeBtn.dataset.productId)
        return
      }

      // Add to cart button
      const addToCartBtn = e.target.closest('[data-bundle-add-to-cart]')
      if (addToCartBtn) {
        e.preventDefault()
        this.addBundleToCart(addToCartBtn)
        return
      }
    })

    // Escape key to close drawers
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const drawer = document.querySelector('[data-bundle-drawer][aria-hidden="false"]')
        if (drawer) {
          this.closeDrawer(drawer)
          this.resetButtonStateForDrawer(drawer)
        }
      }
    })
  }

  resetButtonStateForDrawer(drawer) {
    // Find the button associated with this drawer via aria-controls
    const drawerId = drawer.id
    const button = document.querySelector(`[aria-controls="${drawerId}"]`)

    if (button && !button.disabled && !this.bundle.has(button.dataset.productId)) {
      const bundleRadios = drawer.querySelector('bundle-variant-radios')

      // Reset variant selections to first option
      if (bundleRadios) {
        bundleRadios.querySelectorAll('fieldset').forEach((fieldset) => {
          const firstRadio = fieldset.querySelector('input[type="radio"]')
          if (firstRadio) firstRadio.checked = true
        })
      }

      // Reset to primary state
      button.classList.remove('button--secondary')
      button.classList.add('button--primary')
      const buttonText = button.querySelector('.card-bundle__button-text')
      if (buttonText) buttonText.textContent = 'Select options'
      delete button.dataset.variantId
      delete button.dataset.variantPrice
    }

    // Reset in-drawer add button
    const drawerAddBtn = drawer.querySelector('[data-bundle-drawer-add]')
    if (drawerAddBtn) drawerAddBtn.disabled = true
  }

  handleButtonClick(button) {
    const productId = button.dataset.productId
    console.log(button)
    console.log(productId)
    if (this.bundle.has(productId)) {
      return
    }

    const isMobile = window.innerWidth < 990
    const hasVariant = button.hasAttribute('data-variant-id')
    const drawerId = button.getAttribute('aria-controls')
    const drawer = document.getElementById(drawerId)

    // Check if drawer has color swatches (meaning user can still choose colors)
    const hasColorSwatches = drawer?.querySelector('product-swatches') !== null

    // Check if user has explicitly interacted with the drawer for this product
    const userHasChosen = button.dataset.userChosen === 'true'

    // Check if button is already in "Add to look" state (drawer was opened, user is confirming selection)
    const isReadyToAdd = button.classList.contains('button--secondary')

    // On desktop with variant selected AND no more choices to make: add directly
    // But if there are color swatches and user hasn't chosen yet, open drawer first
    // If button is already in "Add to look" state, user is confirming their selection (even if default)
    console.log(hasVariant)
    console.log(hasColorSwatches)
    console.log(userHasChosen)
    if (!isMobile && hasVariant && (!hasColorSwatches || userHasChosen || isReadyToAdd)) {
      this.addToBundle(button)
      return
    }

    // Otherwise: open drawer
    if (!drawer) return

    this.openDrawer(drawer)

    const bundleRadios = drawer.querySelector('bundle-variant-radios')
    if (!bundleRadios) return

    bundleRadios.updateOptions()
    bundleRadios.updateMasterId()

    if (bundleRadios.currentVariant) {
      // Store variant data on card button
      button.dataset.variantId = bundleRadios.currentVariant.id
      button.dataset.variantPrice = bundleRadios.currentVariant.price

      // Desktop: update card button appearance
      if (!isMobile) {
        button.classList.remove('button--primary')
        button.classList.add('button--secondary')
        const buttonText = button.querySelector('.card-bundle__button-text')
        if (buttonText) buttonText.textContent = 'Add to look'
      }

      // Enable in-drawer add button
      const drawerAddBtn = drawer.querySelector('[data-bundle-drawer-add]')
      if (drawerAddBtn) drawerAddBtn.disabled = false
    }
  }

  addToBundle(button) {
    const drawerId = button.getAttribute('aria-controls')
    const drawer = document.getElementById(drawerId)
    const bundleRadios = drawer?.querySelector('bundle-variant-radios')

    const item = {
      productId: button.dataset.productId,
      productTitle: button.dataset.productTitle,
      productHandle: button.dataset.productHandle,
      productImage: button.dataset.productImage,
      variantId: button.dataset.variantId,
      variantPrice: button.dataset.variantPrice,
      selectedOptions: this.getSelectedOptions(bundleRadios)
    }

    // Preload the image before adding to bundle
    const img = new Image()
    img.src = item.productImage

    const completeAddition = () => {
      // Add to bundle
      this.bundle.set(item.productId, item)

      // Close drawer
      if (drawer) this.closeDrawer(drawer)

      // Reset variant selections
      if (bundleRadios) {
        bundleRadios.querySelectorAll('fieldset').forEach((fieldset) => {
          const firstRadio = fieldset.querySelector('input[type="radio"]')
          if (firstRadio) firstRadio.checked = true
        })
        bundleRadios.dispatchEvent(new Event('change', { bubbles: true }))
      }

      // Update button to "Added to look" state
      button.classList.remove('button--primary', 'button--secondary')
      button.classList.add('button--tertiary')
      const buttonText = button.querySelector('.card-bundle__button-text')
      if (buttonText) buttonText.textContent = 'Added to look'
      button.disabled = true
      delete button.dataset.variantId
      delete button.dataset.variantPrice

      this.renderAddedProducts()
    }

    img.onload = completeAddition
    img.onerror = completeAddition
  }

  getSelectedOptions(bundleRadios) {
    if (!bundleRadios) return {}

    const options = {}
    bundleRadios.querySelectorAll('fieldset').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend')
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked')
      if (legend && checkedInput) {
        options[legend.textContent.trim()] = checkedInput.value
      }
    })
    return options
  }

  removeFromBundle(productId) {
    this.bundle.delete(productId)
    this.resetProductButton(productId)
    this.renderAddedProducts()
  }

  resetProductButton(productId) {
    const button = document.querySelector(`[data-bundle-card-button][data-product-id="${productId}"]`)
    if (button) {
      button.classList.remove('button--secondary', 'button--tertiary')
      button.classList.add('button--primary')
      const buttonText = button.querySelector('.card-bundle__button-text')
      if (buttonText) buttonText.textContent = 'Select options'
      button.disabled = false
      delete button.dataset.variantId
      delete button.dataset.variantPrice
    }
  }

  clearBundle() {
    // Reset all product buttons
    this.bundle.forEach((item, productId) => {
      this.resetProductButton(productId)
    })

    // Clear the bundle
    this.bundle.clear()

    // Update the UI
    this.renderAddedProducts()
  }

  renderAddedProducts() {
    const container = document.querySelector('.product-bundle__added-products')
    if (!container) return

    // Show/hide container
    if (this.bundle.size === 0) {
      container.style.display = 'none'
      return
    } else {
      container.style.display = 'block'
    }

    // Clear all slots first
    const slots = container.querySelectorAll('[data-bundle-slot]')
    slots.forEach((slot) => {
      slot.innerHTML = '<div class="bundle-added-list__item-placeholder"></div>'
    })

    // Fill slots with bundle items
    let slotIndex = 0
    let totalPrice = 0
    this.bundle.forEach((item) => {
      if (slotIndex < 4) {
        const slot = slots[slotIndex]
        slot.innerHTML = `
          <img 
            src="${item.productImage}" 
            alt="${item.productTitle}"
            class="bundle-added-list__item-image"
            loading="lazy">
          <button 
            type="button" 
            class="bundle-added-list__item-remove" 
            data-bundle-remove 
            data-product-id="${item.productId}"
            aria-label="Remove ${item.productTitle}">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        `
        slotIndex++
      }
      totalPrice += parseInt(item.variantPrice)
    })

    // Update total price
    const priceElement = container.querySelector('[data-bundle-price]')
    if (priceElement) {
      const bundleSection = document.querySelector('[data-section-type="product-bundle"]')
      const enableDiscount = bundleSection?.dataset.enableDiscount === 'true'
      const discountPercentage = parseInt(bundleSection?.dataset.discountPercentage) || 0
      const moneyFormat = window.theme?.moneyFormat || '${{amount}}'

      // Show discounted price if discount is enabled and all 4 items are added
      if (enableDiscount && discountPercentage > 0 && this.bundle.size === 4) {
        const discountedPrice = Math.round(totalPrice * (1 - discountPercentage / 100))
        priceElement.innerHTML = `${formatMoney(discountedPrice, moneyFormat)} <s>${formatMoney(totalPrice, moneyFormat)}</s>`
      } else {
        priceElement.textContent = formatMoney(totalPrice, moneyFormat)
      }
    }
  }

  async addBundleToCart(button) {
    if (this.bundle.size === 0) return

    // Prevent double-clicks or multiple simultaneous requests
    if (button.disabled || button.classList.contains('loading')) {
      return
    }

    // Disable button while processing
    button.disabled = true
    button.classList.add('loading')

    // Collect all items to add
    const items = []
    this.bundle.forEach((item) => {
      items.push({
        id: item.variantId,
        quantity: 1
      })
    })

    // Get cart drawer/notification for updating
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer')

    // Prepare request config
    const config = fetchConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'

    const body = { items }

    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id)
      body.sections_url = window.Shopify?.routes?.root || '/'
      cart.setActiveElement(document.activeElement)
    }

    config.body = JSON.stringify(body)

    try {
      const response = await fetch(`${routes.cart_add_url}`, config)
      const responseData = await response.json()

      if (responseData.status) {
        // Handle error
        console.error('Cart error:', responseData.message)
        button.disabled = false
        button.classList.remove('loading')
        return
      }

      // Successfully added to cart
      if (cart) {
        // For bundles, we publish the cart update event but don't set a product ID
        // since renderContents now handles missing product IDs gracefully
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-add' })
        cart.renderContents(responseData)
      } else {
        // If no cart drawer/notification, redirect to cart page
        window.location = window.routes.cart_url
      }

      // Clear the bundle after successful addition
      this.clearBundle()
    } catch (error) {
      console.error('Error adding bundle to cart:', error)
    } finally {
      button.disabled = false
      button.classList.remove('loading')
    }
  }

  openDrawer(drawer) {
    if (window.innerWidth < 990) {
      // Move drawer to body so it escapes any ancestor overflow/transform constraints
      drawer._originalParent = drawer.parentElement
      drawer._originalNextSibling = drawer.nextElementSibling
      document.body.appendChild(drawer)

      // Lock body scroll
      this._scrollY = window.scrollY
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${this._scrollY}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
    }

    // Wait one frame so the DOM move is applied before animating
    requestAnimationFrame(() => {
      drawer.setAttribute('aria-hidden', 'false')
    })
  }

  closeDrawer(drawer) {
    drawer.setAttribute('aria-hidden', 'true')

    if (window.innerWidth < 990) {
      // Unlock body scroll
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      window.scrollTo(0, this._scrollY || 0)

      // Move drawer back to original position after transition ends
      const restore = () => {
        if (drawer._originalParent) {
          if (drawer._originalNextSibling && drawer._originalNextSibling.parentNode === drawer._originalParent) {
            drawer._originalParent.insertBefore(drawer, drawer._originalNextSibling)
          } else {
            drawer._originalParent.appendChild(drawer)
          }
        }
        drawer.removeEventListener('transitionend', restore)
      }

      drawer.addEventListener('transitionend', restore, { once: true })

      // Fallback in case transitionend doesn't fire
      setTimeout(restore, 400)
    }
  }

  setupFeaturedCarousel() {
    const carousel = document.querySelector('[data-featured-carousel]')
    const prev = document.querySelector('[data-featured-prev]')
    const next = document.querySelector('[data-featured-next]')
    if (!carousel || !prev || !next) return

    prev.addEventListener('click', () => {
      carousel.scrollBy({ left: -carousel.offsetWidth * 0.75, behavior: 'smooth' })
    })

    next.addEventListener('click', () => {
      carousel.scrollBy({ left: carousel.offsetWidth * 0.75, behavior: 'smooth' })
    })
  }
}

// Initialize - ensure only one instance exists
if (!window.bundleCardDrawerInstance) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.bundleCardDrawerInstance = new BundleCardDrawer()
    })
  } else {
    window.bundleCardDrawerInstance = new BundleCardDrawer()
  }
}
